import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { compatWrapper } from "@backstage/core-compat-api";
import { Content, Page } from "@backstage/core-components";
import { configApiRef, useApi } from "@backstage/core-plugin-api";
import {
    CustomHomepageGrid,
    HomePageRandomJoke,
    HomePageRecentlyVisited,
    HomePageStarredEntities,
    HomePageToolkit,
    HomePageTopVisited,
    LayoutConfiguration,
    QuickStartCard,
} from "@backstage/plugin-home";
import { SearchBarBase } from "@backstage/plugin-search-react";
import { Grid, Link, Text } from "@backstage/ui";
import { AnnouncementsCard } from "@backstage-community/plugin-announcements";
import { LogoIcon } from "@internal/backstage-plugin-app-react";
import { IncidentHomeCard } from "@internal/backstage-plugin-incidentio-react";
import { PolicyCenterHomeCard } from "@internal/backstage-plugin-policy-center-react";
import { Build, Dashboard, Help } from "@material-ui/icons";

import styles from "./HomePage.module.css";
import { HomePageLogo } from "./HomePageLogo";
import ExampleConfig from "./static/example-config.png";

// Legacy `CustomHomepageGrid` layout: cards are placed by absolute x/y/width/
// height. Two migration notes for the eventual move to the NFS home page:
//   - This grid does not consume `props.widgets`, so any card registered via
//     `HomePageWidgetBlueprint` will not render until this layout is rewritten
//     to arrange widgets from their `CardLayout` hints instead of coordinates.
//   - Prefer expressing a card's sizing intent as a `CardLayout` hint on the
//     card itself (see incidentio-react's `createCardExtension`); those hints
//     survive the migration, whereas the coordinates below do not.
const defaultGridConfig: LayoutConfiguration[] = [
    {
        component: "IncidentHomeCard",
        x: 0,
        y: 0,
        width: 3,
        height: 4,
    },
    {
        component: "PolicyCenterHomeCard",
        x: 3,
        y: 0,
        width: 3,
        height: 4,
    },
    {
        component: "HomePageRandomJoke",
        x: 6,
        y: 0,
        width: 3,
        height: 4,
    },
    {
        component: "HomePageToolkit",
        x: 9,
        y: 0,
        width: 3,
        height: 4,
    },
    {
        component: "HomePageStarredEntities",
        x: 0,
        y: 4,
        width: 3,
        height: 4,
    },
    {
        component: "HomePageTopVisited",
        x: 3,
        y: 4,
        width: 3,
        height: 4,
    },
    {
        component: "HomePageRecentlyVisited",
        x: 6,
        y: 4,
        width: 3,
        height: 4,
    },
];

const tools = [
    {
        url: "https://backstage.io",
        label: "Backstage",
        icon: <LogoIcon />,
    },
    {
        url: "https://bep.jackhenry.com",
        label: "Jack Henry Portal",
        icon: <Dashboard />,
    },
    {
        url: "https://banno.slack.com/archives/C045TAQ0FA9",
        label: "Team Nebula",
        icon: <Help />,
    },
    {
        url: "/entity-validation",
        label: "Entity Validator",
        icon: <Build />,
    },
];

export const HomePage = () => {
    const config = useApi(configApiRef);
    const videoTutorialUrl = config.getOptionalString(
        "app.home.videoTutorialUrl"
    );
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const handleSearchSubmit = useCallback(() => {
        navigate(`/search?query=${encodeURIComponent(searchQuery)}`);
    }, [navigate, searchQuery]);

    return (
        <Page themeId="home">
            <Content>
                <Grid.Root columns="12" gap="6" className={styles.gridRoot}>
                    <Grid.Item colSpan="12" className={styles.centeredItem}>
                        <HomePageLogo />
                    </Grid.Item>
                    <Grid.Item colSpan="12" className={styles.centeredItem}>
                        <SearchBarBase
                            className={styles.searchBar}
                            placeholder="Search"
                            value={searchQuery}
                            onChange={setSearchQuery}
                            onSubmit={handleSearchSubmit}
                        />
                    </Grid.Item>
                    <Grid.Item colSpan={{ initial: "12", md: "6" }}>
                        <QuickStartCard
                            title="Catalog Onboarding"
                            modalTitle="Example Config"
                            docsLinkTitle="Onboarding Guide"
                            docsLink="./docs/default/component/backstage/onboarding/"
                            image={
                                <div className={styles.quickStartImageWrapper}>
                                    <img
                                        src={ExampleConfig}
                                        alt="quick start"
                                    />
                                </div>
                            }
                            cardDescription="Onboarding an entity to the catalog is initiated by adding a catalog-info.yaml file to the root of your repo. Click the image to see an example config and follow our Onboarding Guide below to learn more!"
                            additionalContent={
                                <Text>
                                    New to the process? Get started with our{" "}
                                    <Link
                                        color="info"
                                        href="./docs/default/component/backstage/onboarding/"
                                    >
                                        Onboarding Guide
                                    </Link>{" "}
                                    or watch the{" "}
                                    <Link
                                        color="info"
                                        href={videoTutorialUrl}
                                        target="_blank"
                                    >
                                        video tutorial
                                    </Link>
                                    ! Don't want to manually add your
                                    catalog-info.yaml file? Use our fancy{" "}
                                    <Link
                                        color="info"
                                        href="./create/templates/default/add-entity-to-catalog"
                                    >
                                        Catalog Template
                                    </Link>{" "}
                                    to automatically generate one.
                                </Text>
                            }
                        />
                    </Grid.Item>
                    <Grid.Item colSpan={{ initial: "12", md: "6" }}>
                        {compatWrapper(
                            <AnnouncementsCard
                                active={true}
                                hideStartAt={true}
                                max={3}
                                title="Latest Announcements"
                                variant="fullHeight"
                            />
                        )}
                    </Grid.Item>
                </Grid.Root>
                <CustomHomepageGrid
                    containerPadding={[0, 0]}
                    containerMargin={[24, 24]}
                    title="Your Dashboard"
                    cols={{
                        xl: 12,
                        lg: 12,
                        md: 12,
                        sm: 1,
                        xs: 1,
                        xxs: 1,
                    }}
                    breakpoints={{
                        xl: 1280,
                        lg: 1024,
                        md: 768,
                        sm: 640,
                        xs: 0,
                        xxs: 0,
                    }}
                    config={defaultGridConfig}
                >
                    <HomePageToolkit tools={tools} />
                    <HomePageRecentlyVisited />
                    <HomePageTopVisited />
                    <HomePageStarredEntities />
                    <HomePageRandomJoke />
                    <IncidentHomeCard />
                    <PolicyCenterHomeCard
                        policyCenterUrl={"https://bep.jackhenry.com"}
                    />
                </CustomHomepageGrid>
            </Content>
        </Page>
    );
};
